import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, UserCheck, Trash2, UserCog, Loader2, Target } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { api } from "../lib/api";
import { personMatch } from "../lib/searchText";

const MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const when = (iso) => { try { return new Date(iso).toLocaleString("ar-SA-u-nu-latn", { timeZone: "Asia/Riyadh", dateStyle: "short", timeStyle: "short" }); } catch { return iso; } };

export const useUploadTracker = (params, rev = 0) => {
  const [d, setD] = useState(null);
  const key = JSON.stringify(params);
  useEffect(() => { if (!params) return; api.uploadTracker(params).then(setD).catch(() => setD(null)); }, [key, rev]); // eslint-disable-line
  return d;
};

export const UploaderLine = ({ e, testid }) => e ? (
  <span className="inline-flex items-center gap-1 text-[11px] text-slate-500" data-testid={testid}>
    <UserCheck className="h-3.5 w-3.5 text-emerald-600" /> تم الرفع بواسطة <b className="text-slate-700">{e.by}</b>{e.role ? ` (${e.role})` : ""} · {when(e.at)}{e.file ? ` · ${e.file}` : ""}
  </span>
) : null;

export const UploadEntries = ({ list }) => (
  <div className="space-y-1.5">
    {(list || []).length ? list.map((e, i) => <div key={i} className="rounded-md bg-slate-50 p-1.5 text-[11px] text-slate-600"><b className="text-slate-800">{e.by}</b>{e.role ? ` — ${e.role}` : ""}<br />{when(e.at)}{e.file ? <><br /><span className="text-slate-400">{e.file}</span></> : null}</div>)
      : <p className="text-[11px] text-slate-400">مرفوع (قبل تفعيل سجل الرافعين)</p>}
  </div>
);

const OwnerPicker = ({ params, owner, label, onSaved, testid }) => {
  const [emps, setEmps] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => { if (!emps) api.listEmployees().then((l) => setEmps((l || []).map((e) => ({ id: e.id, name: e.name })))).catch(() => setEmps([])); };
  const save = async (employee_id) => {
    setBusy(true);
    try { await api.setUploadOwner({ section: params.section, platform: params.platform || "", kind: params.kind || "", employee_id, label }); toast.success(employee_id ? "تم تحديد المسؤول" : "تمت إزالة المسؤول"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  const list = (emps || []).filter((e) => personMatch(e, q)).slice(0, 40);
  return (
    <Popover onOpenChange={(o) => o && load()}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`${testid}-owner-btn`} className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-100">
          <UserCog className="h-3.5 w-3.5" /> {owner ? `المسؤول: ${owner.name}` : "تحديد المسؤول"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2 space-y-2" dir="rtl">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث عن موظف" className="h-8 w-full rounded-md border border-slate-200 px-2 text-xs" data-testid={`${testid}-owner-search`} />
        <div className="max-h-56 overflow-y-auto space-y-0.5">
          {!emps ? <Loader2 className="mx-auto h-4 w-4 animate-spin text-slate-300" /> : list.map((e) => (
            <button key={e.id} type="button" disabled={busy} onClick={() => save(e.id)} data-testid={`${testid}-owner-opt-${e.id}`}
              className={`block w-full rounded px-2 py-1 text-right text-xs hover:bg-slate-100 ${owner?.employee_id === e.id ? "bg-blue-50 font-bold text-blue-700" : "text-slate-700"}`}>{e.name}</button>))}
        </div>
        {owner && <button type="button" onClick={() => save("")} className="text-[11px] text-rose-600 underline" data-testid={`${testid}-owner-clear`}>إزالة المسؤول</button>}
      </PopoverContent>
    </Popover>
  );
};

const Compliance = ({ c, testid }) => c?.pct == null ? null : (
  <span data-testid={`${testid}-compliance`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${c.pct >= 90 ? "bg-emerald-100 text-emerald-700" : c.pct >= 70 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>
    <Target className="h-3 w-3" /> الالتزام {c.pct}% ({c.done}/{c.required})
  </span>
);

const Chip = ({ slot, label, ok, miss, list, testid, onDelete }) => {
  const cls = `h-7 min-w-[34px] rounded-md px-1 text-[11px] font-bold tabular-nums ${ok ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : miss ? "bg-rose-50 text-rose-400" : "bg-slate-50 text-slate-300"}`;
  if (!ok) return <span className={`${cls} inline-flex items-center justify-center`} title={miss ? "لم يُرفع" : ""} data-testid={testid}>{label}</span>;
  return (
    <Popover>
      <PopoverTrigger asChild><button type="button" className={cls} data-testid={testid}>✓{label}</button></PopoverTrigger>
      <PopoverContent className="w-56 p-2 space-y-2" dir="rtl"><p className="text-xs font-bold text-slate-700" dir="ltr">{slot}</p><UploadEntries list={list} />
        {onDelete && <button type="button" onClick={() => onDelete(slot)} data-testid={`${testid}-delete`} className="flex w-full items-center justify-center gap-1 rounded-md border border-rose-200 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /> {slot.length === 7 ? "حذف فاتورة هذا الشهر" : "حذف بيانات هذا اليوم"}</button>}
      </PopoverContent>
    </Popover>
  );
};

/** Day (or month) strip of uploaded vs missing files + who uploaded. params: {section, platform, kind, month | year} */
export const UploadTracker = ({ params, rev, title = "متابعة الرفع", testid = "upload-tracker", canEdit = true, onDeleted }) => {
  const [own, setOwn] = useState(0);
  const d = useUploadTracker(params, `${rev}|${own}`);
  if (!d) return null;
  const del = canEdit && params.section !== "keeta_reports" ? async (slot) => {
    if (!window.confirm(slot.length === 7 ? `حذف فاتورة شهر ${slot}؟` : `حذف كل بيانات يوم ${slot} من هذا التقرير؟ لا يمكن التراجع.`)) return;
    try { const r = await api.deleteUploadSlot({ section: params.section, platform: params.platform || "", slot }); toast.success(`تم الحذف (${r.deleted} سجل)`); setOwn((x) => x + 1); onDeleted?.(slot); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  } : null;
  const up = new Set(d.uploaded), miss = new Set(d.missing), monthly = d.freq === "monthly";
  return (
    <div className="space-y-1.5 rounded-xl border border-slate-200 bg-white p-2.5" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="inline-flex items-center gap-1 font-bold text-slate-700"><Upload className="h-3.5 w-3.5 text-blue-600" /> {title} {monthly ? d.year : d.month}</span>
        <span className="text-slate-500">مرفوع <b className="text-emerald-600" data-testid={`${testid}-uploaded`}>{d.uploaded.length}</b> {monthly ? "شهر" : "يوم"}{d.missing.length ? <> · ناقص <b className="text-rose-600" data-testid={`${testid}-missing`}>{d.missing.length}</b></> : ""}</span>
        <Compliance c={d.compliance} testid={testid} />
        {canEdit ? <OwnerPicker params={params} owner={d.owner} label={title} onSaved={() => setOwn((x) => x + 1)} testid={testid} /> : d.owner && <span className="text-[11px] text-blue-700">المسؤول: {d.owner.name}</span>}
        <span className="ms-auto"><UploaderLine e={d.last} testid={`${testid}-last`} /></span>
      </div>
      {(d.monthly_files || []).length > 0 && <div className="flex flex-wrap gap-1" data-testid={`${testid}-monthly`}>{d.monthly_files.map((x) => <span key={x.label} className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">✓ {x.label}</span>)}</div>}
      <div className="flex flex-wrap gap-1">
        {d.slots.map((s) => <Chip key={s} slot={s} label={monthly ? MONTHS[Number(s.slice(5)) - 1] : Number(s.slice(8))} ok={up.has(s)} miss={miss.has(s)} list={d.by[s]} testid={`${testid}-${s}`} onDelete={del} />)}
      </div>
    </div>
  );
};

export const UploadCompliance = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.uploadCompliance().then(setD).catch(() => {}); }, []);
  if (!d?.items?.length) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2" data-testid="upload-compliance-card">
      <p className="flex items-center gap-2 font-heading font-bold text-slate-800"><Target className="h-4 w-4 text-blue-600" /> التزام المسؤولين برفع التقارير · {d.month}</p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {d.items.map((x) => (
          <div key={x.key} className="rounded-xl border border-slate-100 bg-slate-50/60 p-2.5" data-testid={`upload-compliance-${x.key}`}>
            <div className="flex items-center justify-between gap-2"><span className="truncate text-sm font-semibold text-slate-800">{x.name}</span><Compliance c={x} testid={`uc-${x.key}`} /></div>
            <p className="truncate text-[11px] text-slate-500">{x.label}</p>
            {x.missing?.length > 0 && <p className="text-[10px] text-rose-500" dir="ltr">ناقص: {x.missing.map((m) => m.slice(5)).join(" · ")}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};
